import {
  DetailContainer,
  Error,
  Title,
} from './DraftReportCaseAttributes.styles';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker } from 'src/components/moleculesLegacy';
import { fetchDraftReport } from 'src/creators';
import { State } from 'src/types';
import { useDraftCaseAttributes } from './useDraftCaseAttributes';
import { checkValidDateBetweenDates } from './checkValidDateBetweenDates';

interface Props {
  reportID: string;
}

export const DraftReportCaseAttributes = ({ reportID }: Props) => {
  const caseId = useSelector(
    ({ manualCaseCreation }: State) =>
      manualCaseCreation.mccReducer.currentReport?.cases?.[0].id,
  );

  useEffect(() => {
    fetchDraftReport(reportID);
  }, [reportID]);

  const { caseDates, tempCaseDates, errors, handleOnChange, handleOnBlur } =
    useDraftCaseAttributes(reportID, caseId);

  return (
    <>
      <DetailContainer key={'submission-date'}>
        <Title>Submission date (Required)</Title>
        <DatePicker
          max={caseDates.submittedAt.max}
          onChange={(e) => {
            const date = e.target.value;
            const isValidDate = checkValidDateBetweenDates(
              date,
              caseDates.submittedAt.max,
            );
            handleOnChange(date, isValidDate, 'submittedAt');
          }}
          onBlur={() => {
            const date = tempCaseDates.submittedAt;
            const noDate = date === '';
            const isValidDate =
              noDate ||
              checkValidDateBetweenDates(date, caseDates.submittedAt.max);
            handleOnBlur(isValidDate, 'submittedAt');
          }}
          value={tempCaseDates.submittedAt || caseDates.submittedAt.value}
          error={errors.submittedAt}
          data-testid="submitted-date-picker"
        />
        {errors.submittedAt && (
          <Error data-testid="submitted-date-error">
            Please enter a valid date
          </Error>
        )}
      </DetailContainer>
      <DetailContainer key={'read-date'}>
        <Title>Read date</Title>
        <DatePicker
          max={caseDates.readAt.max}
          min={caseDates.readAt.min}
          onChange={(e) => {
            const date = e.target.value;
            const clearDate = date === '';
            const isValidDate =
              clearDate ||
              checkValidDateBetweenDates(
                date,
                caseDates.readAt.max,
                caseDates.readAt.min,
              );
            handleOnChange(date, isValidDate, 'readAt');
          }}
          onBlur={() => {
            const date = tempCaseDates.readAt;
            const clearDate = date === '' || date === null;
            const isValidDate =
              clearDate ||
              checkValidDateBetweenDates(
                date,
                caseDates.readAt.max,
                caseDates.readAt.min,
              );
            handleOnBlur(isValidDate, 'readAt');
          }}
          value={tempCaseDates.readAt ?? caseDates.readAt.value}
          error={errors.readAt}
          data-testid="read-date-picker"
        />
        {errors.readAt && (
          <Error data-testid="read-date-error">Please enter a valid date</Error>
        )}
      </DetailContainer>
      <DetailContainer key={'investigating-date'}>
        <Title>Investigating date</Title>
        <DatePicker
          max={caseDates.investigatingAt.max}
          min={caseDates.investigatingAt.min}
          onChange={(e) => {
            const date = e.target.value;
            const clearDate = date === '';
            const isValidDate =
              clearDate ||
              checkValidDateBetweenDates(
                date,
                caseDates.investigatingAt.max,
                caseDates.investigatingAt.min,
              );
            handleOnChange(date, isValidDate, 'investigatingAt');
          }}
          onBlur={() => {
            const date = tempCaseDates.investigatingAt;
            const clearDate = date === '' || date === null;
            const isValidDate =
              clearDate ||
              checkValidDateBetweenDates(
                date,
                caseDates.investigatingAt.max,
                caseDates.investigatingAt.min,
              );
            handleOnBlur(isValidDate, 'investigatingAt');
          }}
          value={
            tempCaseDates.investigatingAt ?? caseDates.investigatingAt.value
          }
          error={errors.investigatingAt}
          data-testid="investigating-date-picker"
        />
        {errors.investigatingAt && (
          <Error data-testid="investigating-date-error">
            Please enter a valid date
          </Error>
        )}
      </DetailContainer>
      <DetailContainer key={'close-date'}>
        <Title>Close date</Title>
        <DatePicker
          max={caseDates.closedAt.max}
          min={caseDates.closedAt.min}
          onChange={(e) => {
            const date = e.target.value;
            const clearDate = date === '';
            const isValidDate =
              clearDate ||
              checkValidDateBetweenDates(
                date,
                caseDates.closedAt.max,
                caseDates.closedAt.min,
              );
            handleOnChange(date, isValidDate, 'closedAt');
          }}
          onBlur={() => {
            const date = tempCaseDates.closedAt;
            const clearDate = date === '' || date === null;
            const isValidDate =
              clearDate ||
              checkValidDateBetweenDates(
                date,
                caseDates.closedAt.max,
                caseDates.closedAt.min,
              );
            handleOnBlur(isValidDate, 'closedAt');
          }}
          value={tempCaseDates.closedAt ?? caseDates.closedAt.value}
          error={errors.closedAt}
          data-testid="closed-date-picker"
        />
        {errors.closedAt && (
          <Error data-testid="closed-date-error">
            Please enter a valid date
          </Error>
        )}
      </DetailContainer>
    </>
  );
};
